/**
 * Season-aware substitution for curated affiliate picks.
 *
 * A pick can declare the months it is actually in season (`seasonWindow`) plus a
 * replacement to show the rest of the year (`offSeasonAlternate`). Outside the
 * window the alternate's product fields replace the pick's, while the pick keeps
 * its own `insertAfterH2` so placement and the lead-anchor math in
 * `insertProductPickMarkers` never move with the calendar.
 *
 * Two invariants this module exists to hold:
 *
 * 1. Determinism across server and client. Blog picks are resolved in a client
 *    component that also renders on the server, so a month read from the
 *    viewer's local clock would flip the card between the SSR payload and
 *    hydration (React #418) for anyone whose UTC offset crosses a month
 *    boundary. Every read here goes through `getUtcMonth`, which is the same
 *    number everywhere at a given instant.
 * 2. No date arithmetic. Windows are plain 1-12 month integers compared
 *    directly, never Date objects rolled forward or back — the year-shift bug
 *    class that keeps recurring in the calculator date-window helpers cannot
 *    reach code that never constructs a second Date.
 */

/** Inclusive month window, 1 = January through 12 = December. Wraps when start > end (e.g. Nov-Feb). */
export interface SeasonWindow {
  startMonth: number
  endMonth: number
}

/** The product fields a pick swaps in when it is out of season. Placement fields are not overridable. */
export interface SeasonalAlternate<TBadge = unknown> {
  name: string
  affiliateUrl: string
  price: string
  description: string
  imageUrl?: string
  badge?: TBadge
  shortName?: string
}

export interface SeasonallySwappable<TBadge = unknown> {
  seasonWindow?: SeasonWindow
  offSeasonAlternate?: SeasonalAlternate<TBadge>
}

/** Current month as 1-12, read in UTC so server and client always agree. */
export function getUtcMonth(now: Date = new Date()): number {
  return now.getUTCMonth() + 1
}

/** True when `month` (1-12) falls inside the inclusive window, wrapping across the new year. */
export function isMonthInWindow(window: SeasonWindow, month: number): boolean {
  const { startMonth: start, endMonth: end } = window
  if (start <= end) return month >= start && month <= end
  // Wrapped window (e.g. Nov-Feb): in season at or after the start, or at or before the end.
  return month >= start || month <= end
}

/**
 * Returns the pick as it should render this month: unchanged when in season or
 * when it declares no window/alternate, otherwise a copy carrying the
 * alternate's product fields. `month` defaults to the current UTC month; pass it
 * explicitly from a server component to pin the value down the tree.
 */
export function resolveSeasonalPick<TBadge, T extends SeasonallySwappable<TBadge>>(
  pick: T,
  month: number = getUtcMonth(),
): T {
  const { seasonWindow, offSeasonAlternate } = pick
  if (!seasonWindow || !offSeasonAlternate) return pick
  if (isMonthInWindow(seasonWindow, month)) return pick

  // Spread the alternate over the pick so placement fields (insertAfterH2,
  // disableLeadAnchor) survive, and drop the optionals the alternate does not
  // set rather than letting the in-season product's image or CTA name leak
  // onto a different product.
  return {
    ...pick,
    name: offSeasonAlternate.name,
    affiliateUrl: offSeasonAlternate.affiliateUrl,
    price: offSeasonAlternate.price,
    description: offSeasonAlternate.description,
    imageUrl: offSeasonAlternate.imageUrl,
    badge: offSeasonAlternate.badge,
    shortName: offSeasonAlternate.shortName,
  }
}

/** `resolveSeasonalPick` across a list, reading the month once so one list cannot straddle a boundary. */
export function resolveSeasonalPicks<TBadge, T extends SeasonallySwappable<TBadge>>(
  picks: readonly T[] | undefined,
  month: number = getUtcMonth(),
): T[] {
  if (!picks) return []
  return picks.map((pick) => resolveSeasonalPick<TBadge, T>(pick, month))
}
